import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/context/AdminAuthContext";
import { Card, PageTitle, Btn, Empty, Field, Input, Select, Toggle } from "@/admin/ui";

const EMPTY = { code: "", type: "percent", value: "", min_order: 0, max_discount: "", expires_at: "", active: true, description: "" };

const CouponForm = ({ coupon, onSaved, onCancel }) => {
  const [form, setForm] = useState(coupon?.code ? { ...coupon, max_discount: coupon.max_discount ?? "", expires_at: coupon.expires_at ? coupon.expires_at.slice(0, 10) : "" } : EMPTY);
  const [busy, setBusy] = useState(false);
  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || form.value === "") return toast.error("Code and value are required");
    setBusy(true);
    const payload = {
      ...form,
      code: form.code.trim().toUpperCase(),
      value: Number(form.value),
      min_order: Number(form.min_order) || 0,
      max_discount: form.max_discount === "" ? null : Number(form.max_discount),
      expires_at: form.expires_at ? new Date(`${form.expires_at}T23:59:59`).toISOString() : null,
    };
    delete payload.id; delete payload.created_at;
    try {
      coupon?.code ? await adminApi.put(`/admin/coupons/${coupon.code}`, payload) : await adminApi.post("/admin/coupons", payload);
      toast.success(coupon?.code ? "Coupon updated" : "Coupon created");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err, "Could not save coupon"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="coupon-form" onSubmit={save} className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Coupon code"><Input data-testid="coupon-code-input" value={form.code} onChange={on("code")} disabled={!!coupon?.code} placeholder="WELCOME10" className="uppercase" /></Field>
        <Field label="Type">
          <Select data-testid="coupon-type-select" value={form.type} onChange={on("type")}>
            <option value="percent">Percentage off (%)</option>
            <option value="flat">Flat amount off (₹)</option>
          </Select>
        </Field>
        <Field label={form.type === "percent" ? "Discount (%)" : "Discount (₹)"}><Input data-testid="coupon-value-input" type="number" min="0" value={form.value} onChange={on("value")} required /></Field>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Minimum order (₹)"><Input data-testid="coupon-min-input" type="number" min="0" value={form.min_order} onChange={on("min_order")} /></Field>
        <Field label="Max discount (₹)" hint="Optional cap for % coupons"><Input data-testid="coupon-max-input" type="number" min="0" value={form.max_discount} onChange={on("max_discount")} /></Field>
        <Field label="Expires on" hint="Optional"><Input data-testid="coupon-expiry-input" type="date" value={form.expires_at} onChange={on("expires_at")} /></Field>
      </div>
      <Field label="Description shown to customer"><Input data-testid="coupon-description-input" value={form.description} onChange={on("description")} placeholder="10% off your first order" /></Field>
      <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
        <Toggle testId="coupon-active-toggle" checked={form.active} onChange={(v) => setForm((f) => ({ ...f, active: v }))} label="Active" />
        <div className="flex gap-2">
          <Btn type="button" variant="ghost" onClick={onCancel} data-testid="coupon-form-cancel">Cancel</Btn>
          <Btn type="submit" busy={busy} data-testid="coupon-form-save">{coupon?.code ? "Save changes" : "Create coupon"}</Btn>
        </div>
      </div>
    </form>
  );
};

export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => adminApi.get("/admin/coupons").then(({ data }) => setCoupons(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try { await adminApi.delete(`/admin/coupons/${c.code}`); toast.success("Coupon deleted"); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  if (editing !== null) {
    return (
      <div>
        <PageTitle title={editing?.code ? `Edit coupon ${editing.code}` : "New coupon"} sub="Customers enter the code in their basket." />
        <Card className="p-6 sm:p-8"><CouponForm coupon={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} /></Card>
      </div>
    );
  }

  return (
    <div data-testid="admin-coupons-page">
      <PageTitle title="Offers & Coupons" sub="Create discount codes with limits and expiry dates." action={<Btn data-testid="add-coupon-button" onClick={() => setEditing({})}><Plus size={14} /> New coupon</Btn>} />
      <Card className="overflow-hidden">
        {coupons.length === 0 && <Empty text="No coupons yet. Create your first offer." />}
        <ul className="divide-y divide-forest/10">
          {coupons.map((c) => {
            const expired = c.expires_at && new Date(c.expires_at) < new Date();
            return (
              <li key={c.code} data-testid={`admin-coupon-row-${c.code}`} className={`flex items-center gap-4 px-4 sm:px-6 py-4 ${!c.active || expired ? "opacity-50" : ""}`}>
                <span className="font-mono text-sm font-bold text-forest bg-[#FBF6E8] border border-gold/30 px-3 py-1.5 rounded-lg">{c.code}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-forest font-semibold">{c.type === "percent" ? `${c.value}% off` : `₹${c.value} off`}{c.min_order > 0 && ` · min ₹${c.min_order}`}{c.max_discount && ` · max ₹${c.max_discount}`}</p>
                  <p className="text-xs text-mutedgreen truncate">{c.description || "—"}{c.expires_at && ` · ${expired ? "Expired" : "Expires"} ${new Date(c.expires_at).toLocaleDateString("en-IN")}`}{!c.active && " · Inactive"}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`edit-coupon-${c.code}`} onClick={() => setEditing(c)} aria-label="Edit" className="p-2 rounded-full text-forest hover:bg-forest/5"><Pencil size={15} /></button>
                  <button data-testid={`delete-coupon-${c.code}`} onClick={() => remove(c)} aria-label="Delete" className="p-2 rounded-full text-mutedgreen hover:text-red-700 hover:bg-red-50"><Trash2 size={15} /></button>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
}
